import { DevicesDeviceType, type DevicesDevice, type DevicesDeviceState } from '@smarthome/api';

/**
 * Get the ID to use for API calls.
 * Prefers humanId when present (backend supports both).
 */
export function getDeviceIdForApi(device: DevicesDevice): string {
  return device.humanId || device.id || '';
}

/**
 * Get the current on/off state of a device.
 * Returns null if the state cannot be determined.
 */
export function getDeviceOn(device: DevicesDevice): boolean | null {
  const { state, type } = device;
  if (!state || !type) return null;

  switch (type) {
    case DevicesDeviceType.DeviceTypeSwitch:
      return state.switch?.on ?? null;
    case DevicesDeviceType.DeviceTypeLight:
      return state.light?.on ?? null;
    case DevicesDeviceType.DeviceTypeWindowCovering:
      // Window coverings use closedPercentage: 0 = open (on=false), 100 = closed (on=true)
      if (state.windowCovering?.closedPercentage === undefined) return null;
      return state.windowCovering.closedPercentage === 100;
    case DevicesDeviceType.DeviceTypeFan:
      return state.fan?.on ?? null;
    case DevicesDeviceType.DeviceTypeSpeaker:
      return state.speaker?.on ?? null;
    case DevicesDeviceType.DeviceTypeTV:
      return state.tv?.on ?? null;
    default:
      return null;
  }
}

/**
 * Build a state patch for turning a device on/off.
 * Returns the minimal state object needed for the API call.
 */
export function buildOnOffStatePatch(device: DevicesDevice, nextOn: boolean): DevicesDeviceState {
  const { type } = device;

  switch (type) {
    case DevicesDeviceType.DeviceTypeSwitch:
      return { switch: { on: nextOn } };
    case DevicesDeviceType.DeviceTypeLight:
      return { light: { on: nextOn } };
    case DevicesDeviceType.DeviceTypeWindowCovering:
      // Window coverings use closedPercentage: on=true → 100 (closed), on=false → 0 (open)
      return { windowCovering: { closedPercentage: nextOn ? 100 : 0 } };
    case DevicesDeviceType.DeviceTypeFan:
      return { fan: { on: nextOn } };
    case DevicesDeviceType.DeviceTypeSpeaker:
      return { speaker: { on: nextOn } };
    case DevicesDeviceType.DeviceTypeTV:
      return { tv: { on: nextOn } };
    default:
      // Fallback to switch for unknown types
      return { switch: { on: nextOn } };
  }
}

/**
 * Apply a state patch to a device's existing state.
 * Used for optimistic updates.
 */
export function applyStatePatch(
  currentState: DevicesDeviceState | undefined,
  patch: DevicesDeviceState
): DevicesDeviceState {
  return {
    ...currentState,
    ...patch,
    // Merge nested state objects
    switch: patch.switch ? { ...currentState?.switch, ...patch.switch } : currentState?.switch,
    light: patch.light ? { ...currentState?.light, ...patch.light } : currentState?.light,
    windowCovering: patch.windowCovering
      ? { ...currentState?.windowCovering, ...patch.windowCovering }
      : currentState?.windowCovering,
    fan: patch.fan ? { ...currentState?.fan, ...patch.fan } : currentState?.fan,
    speaker: patch.speaker ? { ...currentState?.speaker, ...patch.speaker } : currentState?.speaker,
    tv: patch.tv ? { ...currentState?.tv, ...patch.tv } : currentState?.tv,
  };
}
